import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { ColDef } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import type { TaskTreeNode, TaskItem } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import type { TaskItemResponse } from '@/api/generated/models/TaskItemResponse'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'

interface TaskItemsTableProps {
  workspaceId: string
  selectedBusinessId?: string
  selectedDepartmentId?: string
  selectedMemberId?: string
  isVisible: boolean
  searchQuery?: string
  searchTrigger?: number  // Explicit search trigger
  onTaskItemClick?: (taskItem: TaskTreeNode) => void
  startDate?: Date | null
  endDate?: Date | null
  status?: string  // Status filter: 'incomplete' | 'assigned' | 'completed' | 'reviewed'
  /** 값이 바뀌면 목록을 다시 조회한다 (수정/삭제 후 부모가 증가시킨다) */
  refreshTrigger?: number
}

const TaskItemsTable: React.FC<TaskItemsTableProps> = ({
  workspaceId,
  selectedBusinessId,
  selectedDepartmentId,
  selectedMemberId,
  isVisible,
  searchQuery,
  searchTrigger,
  onTaskItemClick,
  startDate,
  endDate,
  status,
  refreshTrigger
}) => {
  // 인스턴스별 로컬 상태.
  // 전역 스토어를 쓰면 한 화면에 이 테이블이 둘 이상일 때(주간/월간 보고서의 당월·익월 목록)
  // 나중에 끝난 조회가 앞선 조회를 덮어써 두 목록이 같은 데이터를 보여준다.
  const [taskItems, setTaskItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set())
  // 마지막으로 시작한 조회의 번호. 늦게 도착한 이전 응답이 최신 결과를 덮어쓰지 않게 한다.
  const requestSeqRef = useRef(0)

  // TaskItemResponse to TaskTreeNode conversion helper
  const convertTaskItemToTreeNode = useCallback((taskItem: TaskItemResponse): TaskTreeNode => {
    return {
      id: taskItem.id,
      type: 'task_item',
      name: taskItem.name,
      status: taskItem.status,  // Add status field for approval button logic
      business_name: taskItem.business_name || '',
      department_name: taskItem.department_name,
      assignee: taskItem.assignee,
      sub_assignee: taskItem.sub_assignee || undefined,
      manager: taskItem.manager,
      reviewer: taskItem.reviewer,
      orgHierarchy: [],
      originalData: {
        id: taskItem.id,
        type: 'task_item',
        name: taskItem.name,
        start_at: taskItem.start_at,
        end_at: taskItem.end_at,
        status: taskItem.status,  // Include status in originalData as well
        output: taskItem.output,
        department_id: taskItem.department_id,
        department_name: taskItem.department_name,
        assignee: taskItem.assignee,
        sub_assignee: taskItem.sub_assignee || {
          id: '',
          name: '',
          email: '',
          phone: '',
          account_id: ''
        },
        manager: taskItem.manager,
        reviewer: taskItem.reviewer
      } as TaskItem
    }
  }, [])

  // Date formatting helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\./g, '.').replace(/ /g, '')
  }

  // Row click handler
  const handleRowClick = useCallback((event: any) => {
    if (onTaskItemClick && event.data && !event.data.isPlaceholder) {
      const taskItem = event.data as TaskItemResponse
      const treeNode = convertTaskItemToTreeNode(taskItem)
      onTaskItemClick(treeNode)
    }
  }, [onTaskItemClick, convertTaskItemToTreeNode])

  // 목록 조회. reset이면 1페이지부터 새로 채우고, 아니면 해당 페이지 구간만 끼워 넣는다.
  const fetchItems = useCallback(async (page: number, reset: boolean = false) => {
    if (!workspaceId) return

    const seq = ++requestSeqRef.current

    try {
      setError(null)
      if (reset) {
        setIsLoading(true)
        setTaskItems([])
        setLoadedPages(new Set())
      } else {
        setIsLoadingMore(true)
      }

      const response = await TaskItemService.appsTaskViewsListTaskItems({
        workspaceId,
        businessId: selectedBusinessId || undefined,
        departmentId: selectedDepartmentId || undefined,
        stakeholderId: selectedMemberId || undefined,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
        q: searchQuery || undefined,
        status: status || undefined,
        page,
        perPage: pageSize,
      })

      // 이 응답이 도착하기 전에 더 최신 조회가 시작됐다면 결과를 버린다
      if (seq !== requestSeqRef.current) return

      const newItems = response?.data ?? []
      const total = response?.meta?.total ?? 0

      if (reset) {
        // 전체 길이만큼 자리를 만들고 1페이지 구간만 실제 데이터로 채운다 (스파스 배열)
        const sparse: any[] = []
        for (let i = 0; i < total; i++) {
          sparse.push(i < newItems.length
            ? newItems[i]
            : { id: `placeholder-task-item-${i}`, isPlaceholder: true })
        }
        setTaskItems(sparse)
      } else {
        const startIndex = (page - 1) * pageSize
        setTaskItems(prev => {
          const updated = [...prev]
          while (updated.length < total) {
            updated.push({ id: `placeholder-task-item-${updated.length}`, isPlaceholder: true })
          }
          for (let i = 0; i < newItems.length; i++) {
            updated[startIndex + i] = newItems[i]
          }
          return updated
        })
      }

      setTotalItems(total)
      setLoadedPages(prev => new Set([...prev, page]))
    } catch (err) {
      if (seq !== requestSeqRef.current) return
      console.error('Failed to fetch task items:', err)
      setError('수행업무 목록을 불러오는데 실패했습니다.')
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    }
  }, [workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchQuery, status, pageSize])

  // 필터/검색/기간이 바뀌면 처음부터 다시 조회
  useEffect(() => {
    if (isVisible && workspaceId) {
      fetchItems(1, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchQuery, searchTrigger, status, pageSize, refreshTrigger])

  // 페이지 전환 — 아직 안 받은 구간이면 그 페이지만 채워 넣는다
  const handlePaginationChanged = useCallback((params: any) => {
    const api = params.api
    if (!api || !workspaceId) return

    const currentPageNum = api.paginationGetCurrentPage() + 1
    const currentPageSize = api.paginationGetPageSize()

    if (currentPageSize !== pageSize) {
      setPageSize(currentPageSize)   // pageSize 변경은 위 useEffect가 재조회를 트리거한다
      return
    }

    if (isLoading || isLoadingMore || loadedPages.has(currentPageNum)) return

    const startIndex = (currentPageNum - 1) * pageSize
    const actualEndIndex = Math.min(startIndex + pageSize, totalItems)

    let needsLoad = actualEndIndex > taskItems.length
    if (!needsLoad) {
      for (let i = startIndex; i < actualEndIndex; i++) {
        if (!taskItems[i] || taskItems[i].isPlaceholder) {
          needsLoad = true
          break
        }
      }
    }

    if (needsLoad) {
      fetchItems(currentPageNum, false)
    }
  }, [workspaceId, pageSize, taskItems, totalItems, isLoading, isLoadingMore, loadedPages, fetchItems])

  // Column definitions with specified fields
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '수행업무상태',
      field: 'status',
      width: 100,
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-16"></div>
        }
        if (!params.value) return '-'

        const getStatusColor = (status: string) => {
          switch (status) {
            case 'incomplete':
              return 'error'
            case 'completed':
              return 'success'
            case 'reviewed':
              return 'info'      // 완료(검토자) - 파란색
            case 'assigned':
              return 'warning'   // 미완료(관리자) - 노란색
            default:
              return 'gray'
          }
        }

        const getStatusText = (status: string) => {
          switch (status) {
            case 'incomplete':
              return '미완료 (담당자)'
            case 'completed':
              return '완료 (관리자)'
            case 'reviewed':
              return '완료 (검토자)'
            case 'assigned':
              return '미완료 (관리자)'
            default:
              return status
          }
        }

        return (
          <div className="flex items-center h-full">
            <Tag color={getStatusColor(params.value)} size="small">
              {getStatusText(params.value)}
            </Tag>
          </div>
        )
      }
    },
    {
      headerName: '사업명',
      field: 'business_name',
      width: 150,
      minWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '전략과제명',
      field: 'task_group_name',
      flex: 2,
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Placeholder row handling
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[6],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '실행과제명',
      field: 'task_subgroup_name',
      flex: 2,
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Placeholder row handling
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[6],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '세부실행과제명',
      field: 'task_name',
      flex: 2,
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Placeholder row handling
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[6],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '수행업무명',
      field: 'name',
      flex: 2,
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Placeholder row handling
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[6],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '부서',
      field: 'department_name',
      width: 150,
      minWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '담당자',
      field: 'assignee',
      width: 120,
      minWidth: 120,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        if (params.data?.isPlaceholder) return ''
        return params.data?.assignee?.name || '-'
      },
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '관리자',
      field: 'manager',
      width: 120,
      minWidth: 120,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        if (params.data?.isPlaceholder) return ''
        return params.data?.manager?.name || '-'
      },
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '시작일',
      field: 'start_at',
      width: 120,
      minWidth: 120,
      sortable: false,
      filter: false,
      valueFormatter: (params: any) => {
        if (params.data?.isPlaceholder) return ''
        return formatDate(params.value)
      },
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '종료일',
      field: 'end_at',
      width: 120,
      minWidth: 120,
      sortable: false,
      filter: false,
      valueFormatter: (params: any) => {
        if (params.data?.isPlaceholder) return ''
        return formatDate(params.value)
      },
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    }
  ], [])

  // 조회 실패 안내. 이미 받아 둔 목록이 있으면(페이지 조회만 실패한 경우) 그리드를 유지한다.
  if (error && taskItems.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div
          className="flex items-center justify-center h-32 text-center"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.error.DEFAULT,
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataGrid
        columnDefs={columnDefs}
        rowData={taskItems}
        rowModelType="clientSide"
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[20, 50, 100]}

        // Server total count display
        localeText={{
          ...AG_GRID_LOCALE_KR,
          paginationOf: `총 ${totalItems.toLocaleString()}개`,
          page: '페이지',
          to: '-',
          of: `중`
        }}

        // Pagination event handler
        onPaginationChanged={handlePaginationChanged}

        // Row ID configuration
        getRowId={(params: any) => {
          if (!params.data) return null
          return params.data.id
        }}

        onRowClicked={handleRowClick}
        loading={isLoading || isLoadingMore}
        headerHeight={40}
        rowHeight={42}
        height={40 + (20 * 42) + 60 + 2} // header + (10 rows * rowHeight) + pagination height + border
        suppressMovableColumns={true}
        suppressMenuHide={true}

        // 범위 선택 기능 활성화
        enableRangeSelection={true}
        enableCellCopy={true}
        suppressRowClickSelection={true}
        rangeCopyFormatter={(colId, value) => {
          // 상태 컬럼 포맷팅
          if (colId === 'status') {
            switch (value) {
              case 'incomplete': return '미완료 (담당자)'
              case 'completed': return '완료 (관리자)'
              case 'reviewed': return '완료 (검토자)'
              case 'assigned': return '미완료 (관리자)'
              default: return value || '-'
            }
          }
          // 담당자 컬럼 (객체에서 name 추출)
          if (colId === 'assignee') {
            if (value && typeof value === 'object' && 'name' in value) {
              return value.name || '-'
            }
            return '-'
          }
          // null/undefined 처리
          if (value === null || value === undefined) return '-'
          return value.toString()
        }}
      />
    </div>
  )
}

export default TaskItemsTable